import React from 'react';
import { TableCell, TableRow } from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Mail,
  Phone,
  Building2,
  User,
  MapPin,
  Star
} from 'lucide-react';

export const ClientRow = ({ client, onEdit, onDelete, onView }) => {
  // Helper function to display data with fallback
  const displayData = (data, fallback = '—') => {
    return data && data.trim() !== '' ? data : fallback;
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return 'C';
    return name.split(' ').map(word => word.charAt(0)).join('').slice(0, 2).toUpperCase();
  };

  // Get status badge variant
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700 hover:bg-green-100">
            Activo
          </Badge>
        );
      case 'inactive':
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700 hover:bg-red-100">
            Inactivo
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700 hover:bg-yellow-100">
            Pendiente
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-slate-600 dark:text-slate-400">
            {status || 'Sin estado'}
          </Badge>
        );
    }
  };

  // Get type badge
  const getTypeBadge = (isCompany) => {
    if (isCompany) {
      return (
        <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700 hover:bg-blue-100">
          <Building2 className="h-3 w-3 mr-1" />
          Empresa
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700 hover:bg-purple-100">
          <User className="h-3 w-3 mr-1" />
          Persona
        </Badge>
      );
    }
  };

  return (
    <TableRow className="hover:bg-orange-50/30 dark:hover:bg-orange-900/10 transition-colors duration-200 group border-b border-slate-200/60 dark:border-slate-700/60">
      {/* Name Column */}
      <TableCell className="py-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md ring-2 ring-orange-100 dark:ring-orange-900/30">
            {getInitials(client.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 dark:text-slate-100 truncate group-hover:text-orange-800 dark:group-hover:text-orange-200 transition-colors">
              {displayData(client.name, 'Sin nombre')}
            </div>
            {client.contact_person && (
              <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                Contacto: {client.contact_person}
              </div>
            )}
            {client.tax_id && (
              <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                NIT: {client.tax_id}
              </div>
            )}
          </div>
        </div>
      </TableCell>

      {/* Email Column */}
      <TableCell className="py-4">
        <div className="space-y-1">
          {client.email ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center space-x-2 text-sm text-slate-700 dark:text-slate-300 hover:text-orange-600 dark:hover:text-orange-400 cursor-pointer transition-colors">
                    <Mail className="h-4 w-4 text-orange-500 flex-shrink-0" />
                    <span className="truncate max-w-40">{client.email}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Enviar email a {client.email}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="text-slate-400 dark:text-slate-500 text-sm">—</span>
          )}
          
          {client.contact_email && client.contact_email !== client.email && (
            <div className="flex items-center space-x-2 text-xs text-slate-500 dark:text-slate-400">
              <Mail className="h-3 w-3" />
              <span className="truncate max-w-40">{client.contact_email}</span>
            </div>
          )}
        </div>
      </TableCell>

      {/* Phone Column */}
      <TableCell className="py-4">
        <div className="space-y-1">
          {client.phone ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center space-x-2 text-sm text-slate-700 dark:text-slate-300 hover:text-orange-600 dark:hover:text-orange-400 cursor-pointer transition-colors">
                    <Phone className="h-4 w-4 text-orange-500 flex-shrink-0" />
                    <span className="truncate">{client.phone}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Llamar a {client.phone}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="text-slate-400 dark:text-slate-500 text-sm">—</span>
          )}
          
          {client.contact_phone && client.contact_phone !== client.phone && (
            <div className="flex items-center space-x-2 text-xs text-slate-500 dark:text-slate-400">
              <Phone className="h-3 w-3" />
              <span className="truncate">{client.contact_phone}</span>
            </div>
          )}
        </div>
      </TableCell>

      {/* Country Column */}
      <TableCell className="py-4">
        {client.country ? (
          <div className="flex items-center space-x-2 text-sm text-slate-700 dark:text-slate-300">
            <MapPin className="h-4 w-4 text-orange-500 flex-shrink-0" />
            <span className="truncate">{client.country}</span>
          </div>
        ) : (
          <span className="text-slate-400 dark:text-slate-500 text-sm">—</span>
        )}
        
        {client.city && (
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 truncate">
            {client.city}
          </div>
        )}
      </TableCell>

      {/* Type Column */}
      <TableCell className="py-4 text-center">
        {getTypeBadge(client.is_company)}
        
        {client.industry && (
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 truncate">
            {client.industry}
          </div>
        )}
      </TableCell>

      {/* Status Column */}
      <TableCell className="py-4 text-center">
        {getStatusBadge(client.status)}
        
        {client.priority && client.priority !== 'medium' && (
          <div className="mt-1">
            <Badge 
              variant="outline" 
              className={`text-xs ${
                client.priority === 'high' 
                  ? 'border-red-300 text-red-700 dark:border-red-700 dark:text-red-300' 
                  : 'border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-400'
              }`}
            >
              <Star className="h-3 w-3 mr-1" />
              {client.priority === 'high' ? 'Alta' : 'Baja'}
            </Badge>
          </div>
        )}
      </TableCell>

      {/* Actions Column */}
      <TableCell className="py-4 text-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 hover:bg-orange-50 dark:hover:text-slate-300 dark:hover:bg-orange-900/20 transition-colors"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {onView && (
              <>
                <DropdownMenuItem 
                  onClick={() => onView(client)}
                  className="cursor-pointer text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  <Eye className="h-4 w-4 mr-2 text-slate-500" />
                  Ver detalles
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            
            <DropdownMenuItem 
              onClick={() => onEdit?.(client)}
              className="cursor-pointer text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20"
            >
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuItem 
              onClick={() => onDelete?.(client)}
              className="cursor-pointer text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}; 